/**
 * Reine Ableitungen des Tonhöhen-Tooltips (`NoteHoverTooltip.vue`): wo die Box am Zeiger sitzt und
 * wann sie ganz zu unterbleiben hat.
 *
 * Die **Beschriftung** steht bewusst NICHT hier – dafür gibt es bereits
 * {@link ./notePitchLabel!notePitchLabel}, das die tragende Eingabe/Ausgabe-Unterscheidung kodiert
 * (Ausgabe = klingender Ton, kein Rückfall auf die notierte Staff-Position). Eine zweite Fassung
 * driftet; der Tooltip ruft denselben Helfer wie das Rechtsklick-Menü.
 */

/** Versatz des Tooltips gegenüber dem Zeiger-Hotspot (px).
 *
 *  `noteAtWithinThreshold` prüft nur **horizontal** (`tolX`), vertikal gar nicht – der Zeiger steht
 *  beim Treffer also oft direkt auf dem Notenkopf. Die Oberkante muss daher unter Zeigergrafik
 *  (~20 px) und Notenkopf liegen, sonst verdeckt der Tooltip genau die Note, die er benennt.
 *  Deshalb ausdrücklich **nicht** der Versatz 0 des `NoteActionsOverlay`: dessen Menü soll mit der
 *  Ecke am Cursor kleben, ein Tooltip nicht. */
export const HOVER_TOOLTIP_OFFSET_X = 14;
export const HOVER_TOOLTIP_OFFSET_Y = 20;

/** Mindestabstand zum Viewport-Rand (px). */
export const HOVER_TOOLTIP_MARGIN = 8;

/** Maße für den ersten Frame, solange der ResizeObserver noch 0 misst (das `v-if` hat die Box
 *  gerade erst eingefügt). Ohne sie ragte der Tooltip am Rand für einen Frame sichtbar heraus. */
export const HOVER_TOOLTIP_FALLBACK_WIDTH = 64;
export const HOVER_TOOLTIP_FALLBACK_HEIGHT = 24;

export interface HoverTooltipPositionInput {
    /** Client-Koordinaten des Zeigers. */
    x: number;
    y: number;
    /** Gemessene Tooltip-Maße; `<= 0` ⇒ Fallback. */
    width: number;
    height: number;
    viewportWidth: number;
    viewportHeight: number;
}

/** Viewport-fixe Position der Tooltip-Box: rechts unter dem Zeiger, bei Platzmangel auf die
 *  jeweils andere Seite gekippt, immer vollständig im Viewport. */
export function hoverTooltipPosition({
    x,
    y,
    width,
    height,
    viewportWidth,
    viewportHeight,
}: HoverTooltipPositionInput): {left: number; top: number} {
    const w = width > 0 ? width : HOVER_TOOLTIP_FALLBACK_WIDTH;
    const h = height > 0 ? height : HOVER_TOOLTIP_FALLBACK_HEIGHT;

    // Kippen statt nur klemmen: am Rand geklemmt läge die Box sonst genau auf dem Notenkopf.
    let left = x + HOVER_TOOLTIP_OFFSET_X;
    if (left + w > viewportWidth - HOVER_TOOLTIP_MARGIN) left = x - HOVER_TOOLTIP_OFFSET_X - w;

    let top = y + HOVER_TOOLTIP_OFFSET_Y;
    if (top + h > viewportHeight - HOVER_TOOLTIP_MARGIN) top = y - HOVER_TOOLTIP_OFFSET_Y - h;

    return {
        left: clampToViewport(left, w, viewportWidth),
        top: clampToViewport(top, h, viewportHeight),
    };
}

/** Hält `start` so, dass `size` vollständig sichtbar bleibt; bei zu kleinem Viewport gewinnt der
 *  Rand-Abstand (nie negativ). */
function clampToViewport(start: number, size: number, extent: number): number {
    const maxStart = Math.max(HOVER_TOOLTIP_MARGIN, extent - size - HOVER_TOOLTIP_MARGIN);
    return Math.min(Math.max(HOVER_TOOLTIP_MARGIN, start), maxStart);
}

/** Was den Tooltip verdrängt: das eine schwebende Overlay der Vorschau (`activeOverlay`,
 *  'none' = keins) plus die beiden Zustände, die KEINE Overlays im Enum sind – die
 *  Gesangstext-Sitzung (lebt im Lyric-Composable) und der Quelltext-Editor (Split-Ansicht). */
export interface HoverTooltipSuppressionInput {
    activeOverlay: 'none' | 'actions' | 'gripDialog' | 'bass' | 'shortcuts';
    lyricEntryActive: boolean;
    sourceEditorOpen: boolean;
    /** Der Lösch-Dialog („Pause oder nachrücken?") ist offen. */
    deleteChoiceOpen?: boolean;
}

/** Ist ein Overlay offen (oder Gesangstext/Quelltext-Editor aktiv), bleibt der Tooltip aus.
 *
 *  Nicht nur kosmetisch: alle diese Zustände liegen mit einem ganzflächigen Backdrop bzw.
 *  `pointer-events-auto` über dem Notencontainer und **verschlucken `mousemove`**. Der
 *  Hover-Zustand friert damit auf seinem letzten Wert ein – ohne diese Abfrage hinge die Box
 *  sichtbar neben dem offenen Menü fest. */
export function hoverTooltipSuppressed(input: HoverTooltipSuppressionInput): boolean {
    return input.activeOverlay !== 'none'
        || input.lyricEntryActive
        || input.sourceEditorOpen
        || !!input.deleteChoiceOpen;
}
